import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { readAllRowsKeyset } from '@/lib/supabase-pagination';

export interface HeyReachSender {
  id: string;
  display_name: string;
  email: string | null;
  is_active: boolean;
  ai_drafts_enabled: boolean | null;
}

export interface HeyReachSenderWithCount extends HeyReachSender {
  pending_count: number;
}

interface UseSendersOptions {
  includeInactive?: boolean;
}

/**
 * The seat roster.
 *
 * os_heyreach_senders carries BOTH organization_id and user_id (migrations 005
 * and 015), and its RLS policy is ROLE-aware, not merely org-aware: owners and
 * admins see every seat in the organization, members see only the seat whose
 * user_id is their own (015 "heyreach_senders_user_isolation"). The visible row
 * set is therefore a function of (user, organization) — a cache key naming only
 * the filter options would let an admin's full roster be served to a member of
 * the same organization, and a member's single-seat roster be served to the
 * admin, on nothing more than a re-render after a seat switch.
 */
export const useSenders = (options: UseSendersOptions = {}) => {
  const { includeInactive = false } = options;
  const { user, organizationId } = useAuth();
  const userId = user?.id ?? null;

  return useQuery({
    queryKey: ['heyreach-senders', userId, organizationId, { includeInactive }],
    queryFn: async (): Promise<HeyReachSender[]> => {
      if (organizationId === null) return [];

      // Explicit organization predicate even though RLS already scopes this
      // table: RLS visibility can briefly exceed the session's context org
      // during a membership transition (a user added to org B while still
      // active in org A), and a foreign-org seat picked up in that window
      // would land in org A's cache and, worse, in the Settings roster where
      // seats are renamed and toggled. Defense-in-depth, not a replacement
      // for RLS.
      //
      // Paginated via readAllRowsKeyset: this is the seat ROSTER — a domain list the
      // Settings page renders in full (includeInactive: true, so soft-disabled
      // seats are never pruned from the row count) and from which an operator
      // picks the seat to edit. An unbounded select is capped at PostgREST's
      // 1,000-row default page and returns the truncated page with a 200 and
      // no warning, so a seat past the cap would simply not exist as far as
      // the UI is concerned. `id` is the unique, immutable cursor; the mutable
      // display_name never controls a page boundary.
      const senders = await readAllRowsKeyset<HeyReachSender, string>(
        () => {
          let pageQuery = supabase
            .from('os_heyreach_senders')
            .select('id, display_name, email, is_active, ai_drafts_enabled')
            .eq('organization_id', organizationId);

          if (!includeInactive) {
            pageQuery = pageQuery.eq('is_active', true);
          }

          return pageQuery;
        },
        'id',
        (sender) => sender.id,
        (error) => {
          throw error;
        },
      );

      // Display order is restored here, where no page boundary depends on it.
      return senders.sort((left, right) => (
        left.display_name.localeCompare(right.display_name)
        || left.id.localeCompare(right.id)
      ));
    },
    enabled: userId !== null && organizationId !== null,
    staleTime: 60000,
  });
};

/**
 * The seat roster plus each seat's actionable pending-draft count.
 *
 * get_senders_with_pending_counts(p_organization_id) is SECURITY INVOKER, so both the seat rows
 * and the counts are produced under the caller's RLS on top of the organization predicate — which is role-aware on os_heyreach_senders and seat-scoped on
 * os_linkedin_drafts (migration 015). Two members of one organization get
 * different rows AND different counts from the identical call, so the
 * authenticated user id must be part of the cache key.
 */
export const useSendersWithCounts = () => {
  const { user, organizationId } = useAuth();
  const userId = user?.id ?? null;

  return useQuery({
    queryKey: ['heyreach-senders-with-counts', userId, organizationId],
    queryFn: async (): Promise<HeyReachSenderWithCount[]> => {
      // DIFF r4 F5 — the org-scoped overload, added by
      // 20260811020000_senders_with_pending_counts_org_scoped.sql. The zero-argument version read
      // every sender and every joined draft the CALLER could see, with no organization predicate:
      // the right boundary for a USER, the wrong one for a SCREEN. The Inbox renders in the context
      // of ONE organization and caches under that organization's key, so a member of two would have
      // seen A+B counts stored as A's. The predicate now lives inside the function, with a
      // membership check, so it cannot be argued away by a client that forgets to pass it.
      const { data, error } = await supabase.rpc('get_senders_with_pending_counts', {
        p_organization_id: organizationId,
      });
      if (error) throw error;
      return data || [];
    },
    enabled: userId !== null && organizationId !== null,
    staleTime: 30000,
  });
};
